import io from 'socket.io-client';
import { useWebsocketState, useAction, useActionExt } from './store.util';
import {
  difference as _difference,
  differenceBy as _differenceBy,
  uniq as _uniq,
  throttle as _throttle
} from 'lodash';
import { Subject } from 'rxjs';
import { distinctUntilChanged, tap, filter, map } from 'rxjs/operators';
import Vue from 'vue';
import { EVENTS as WebSocketEvents } from '@/store/modules/websocket/websocket.state';

/**
 * this hook expect user to connectToWs and disconnectWs manually
 */

type RoomId = number | string;

interface ListenOnceToOption {
  timeout?: number;
  roomIdToLeave?: RoomId;
}

const DEFAULT_ROOM_JOIN_TIMEOUT_MS = 5_000;

const SocketClient = io(process.env.VUE_APP_BACKEND_HOSTNAME!, {
  autoConnect: false
});

const activeRoomIds = new Set<RoomId>();

const joinRoom$ = new Subject<RoomId>();

joinRoom$
  .pipe(
    distinctUntilChanged(),
    tap((roomId) => activeRoomIds.add(roomId))
  )
  .subscribe((roomId) => {
    SocketClient.emit('JOIN_ROOM', {
      roomId
    });
  });

export function createWebsocket() {
  const socketConnect = useActionExt('websocket/socketConnect');
  const socketDisconnect = useActionExt('websocket/socketDisconnect');
  const socketException = useActionExt('websocket/socketException');
  const add1UsageCount = useActionExt('websocket/add1UsageCount');
  const minus1UsageCount = useActionExt('websocket/minus1UsageCount');

  const onConnected = () => {
    if (!SocketClient.connected) {
      socketConnect();
      // tslint:disable-next-line
      console.log(`WS connected`);
    }
  };
  const onDisConnected = () => {
    if (SocketClient.connected) {
      socketDisconnect();
      // tslint:disable-next-line
      console.log(`WS disconnected`);
    }
  };
  const onException = (e: Error) => {
    socketException(e);
    // tslint:disable-next-line
    console.log(`WS error:`, e);
  };

  const connectToWs = () => {
    return new Promise((resolve, _) => {
      if (SocketClient.connected) {
        resolve(true);
        return;
      }

      SocketClient.on('connect', onConnected);
      SocketClient.on('disconnect', onDisConnected);
      SocketClient.on('timeout', onException);
      SocketClient.on('error', onException);

      SocketClient.once('connect', resolve);
      SocketClient.open();
      add1UsageCount();
    });
  };

  const disconnectWs = async () => {
    return new Promise((resolve, _) => {
      if (SocketClient.disconnected) {
        resolve(true);
        return;
      }

      SocketClient.off('connect', onConnected);
      SocketClient.off('disconnect', onDisConnected);
      SocketClient.off('timeout', onException);
      SocketClient.off('error', onException);

      SocketClient.once('disconnect', resolve);

      SocketClient.close();
      minus1UsageCount();
    });
  };

  const joinRoomAsync = (
    roomId: RoomId,
    { timeout }: ListenOnceToOption = {
      timeout: DEFAULT_ROOM_JOIN_TIMEOUT_MS
    }
  ) => {
    return new Promise((resolve, reject) => {
      let timeoutId = setTimeout(
        () =>
          reject(
            new Error(`Failed to join room: ${roomId} (timeout: ${timeout}ms)`)
          ),
        timeout
      );

      // subscribe to the server acknowledgement
      SocketClient.once(
        `${WebSocketEvents.JOINED_ROOM}_${roomId}`,
        ({ roomId }: { roomId: RoomId }) => {
          if (timeoutId) clearTimeout(timeoutId);

          activeRoomIds.add(roomId);
          resolve(`Successfully joined room: ${roomId}`);
        }
      );

      SocketClient.emit(WebSocketEvents.JOIN_ROOM, {
        roomId
      });
    });
  };

  // handle add removing event listener
  const listenOnceTo = <T>(event: string, options?: ListenOnceToOption) => {
    return new Promise<T>((resolve, reject) => {
      const { roomIdToLeave, timeout } = {
        // 2 minutes
        timeout: 120_000,
        ...options
      };

      const timer = setTimeout(() => {
        reject(new Error('Event Timeout'));
      }, timeout);

      SocketClient.once(event, (data: T) => {
        clearTimeout(timer);
        resolve(data);

        if (roomIdToLeave) {
          SocketClient.emit(WebSocketEvents.LEAVE_ROOM, {
            roomId: roomIdToLeave
          });
        }
      });
    });
  };

  const removeWorkerJob = useActionExt('websocket/removeWorkerJob');

  return {
    connectToWs,
    disconnectWs,
    SocketClient,
    removeWorkerJob,
    joinRoomAsync,
    listenOnceTo
  };
}

/**
 * @deprecated This function is deprecated and will be removed in the future.
 * Use `createWebsocket()` instead.
 */
export function useWebsocket(this: Vue) {
  console.warn(
    'Warning: useWebsocket is deprecated and will be removed in the future.'
  );
  const socketConnect = useAction.call(this, 'websocket/socketConnect');
  const socketDisconnect = useAction.call(this, 'websocket/socketDisconnect');
  const socketException = useAction.call(this, 'websocket/socketException');
  const add1UsageCount = useAction.call(this, 'websocket/add1UsageCount');
  const minus1UsageCount = useAction.call(this, 'websocket/minus1UsageCount');

  const onConnected = () => {
    if (!useWebsocketState.call(this).isConnected) {
      socketConnect();
      // tslint:disable-next-line
      console.log(`WS connected`);
    }
  };
  const onDisConnected = () => {
    if (useWebsocketState.call(this).isConnected) {
      socketDisconnect();
      // tslint:disable-next-line
      console.log(`WS disconnected`);
    }
  };
  const onException = (e: Error) => {
    socketException(e);
    // tslint:disable-next-line
    console.log(`WS error:`, e);
  };

  const connectToWs = () => {
    return new Promise((resolve, _) => {
      add1UsageCount();
      if (useWebsocketState.call(this).isConnected) {
        resolve(true);
        return;
      }
      SocketClient.open();
      SocketClient.on('connect', onConnected);
      SocketClient.on('disconnect', onDisConnected);
      SocketClient.on('timeout', onException);
      SocketClient.on('error', onException);

      SocketClient.once('connect', resolve);
    });
  };

  const disconnectWs = () => {
    return new Promise((resolve, _) => {
      minus1UsageCount();
      if (
        !useWebsocketState.call(this).isConnected ||
        useWebsocketState.call(this).usageCount > 0
      ) {
        resolve(true);
        return;
      }
      SocketClient.close();
      SocketClient.off('connect', onConnected);
      SocketClient.off('disconnect', onDisConnected);
      SocketClient.off('timeout', onException);
      SocketClient.off('error', onException);

      SocketClient.once('disconnect', resolve);
    });
  };

  // handle joining room
  const joinRoom = (roomId: RoomId) => {
    joinRoom$.next(roomId);
  };
  // handle add removing event listener
  const listenOnceTo = (event: string, option?: ListenOnceToOption) => {
    return new Promise((resolve, reject) => {
      const {
        // 2 mintues
        timeout = 120000
      } = option || {};

      const timer = setTimeout(() => {
        reject(new Error('Event Timeout'));
      }, timeout);

      SocketClient.once(event, (data: any) => {
        clearTimeout(timer);
        resolve(data);
      });
    });
  };

  const removeWorkerJob = useAction.call(this, 'websocket/removeWorkerJob');

  return {
    connectToWs,
    disconnectWs,
    SocketClient,
    removeWorkerJob,
    joinRoom,
    listenOnceTo
  };
}
